import React, { useEffect, useMemo, useState } from "react";
import { Button, Grid } from "@material-ui/core";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import LoadingButton from "../../shared-components/loading-button/LoadingButton";
import { toggleQuickPanel } from "app/main/shared-components/quick-panel/store";
import { createAgentsStyles } from "./create-agents.styles";
import { AppState } from "app/store";
import {
  resetAgentForm,
  fillAgentFormField,
} from "../store/agent-form.slice";

import AgentInformation from "../components/AgentInformation";
import InvoiceInfo from "../components/InvoiceInfo";
import ReservationInfo from "../components/ReservationInfo";
import DebtInfo from "../components/DebtInfo";
import DiscountInfo from "../components/DiscountInfo";
import ScopesInfo from "../components/ScopesInfo";
import ProvisionInfo from "../components/ProvisionInfo";
import Credits from "../components/Credits";

import { useTranslation } from "react-i18next";
import {
  resetAgentLogoImage,
  updateAgentImage,
} from "../store/agent-logo-image.slice";

import { AgentService } from "../../../services";
import AppUtils from "../../../utils/AppUtils";
import {
  fetchAllAgents,
  fetchAllSubAgents,
} from "../store/agents-list.slice";
import { setError } from "../../customer/store/create-costumer.slice";
import { errorMsg, successMsg } from "../../../utils/MessageUtils";
import { setSuccess } from "../store/update-agent.slice";

import useAuthority from "app/hooks/useAuthority";
import { ROLES } from "app/constants/roles";
import { getAccount } from "app/account/store/get-account.slice";

const CreateAgent = () => {
  const classes = createAgentsStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const agentForm = useSelector((state: AppState) => state.agents.agentForm);

  const createAgentLoading = useSelector(
    (state: AppState) => state.agents.createAgent.loading
  );

  const agentLogoImage = useSelector(
    (state: AppState) => state.agents.agentLogoImage
  );

  const isQuickPanelOpen = useSelector((state: any) => state.quickPanel);

  const account = useSelector((state: AppState) => {
    const s: any = state as any;
    return s.account?.account?.account ?? null;
  });

  const { hasUserAnyAuthority } = useAuthority();

  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);
  const isAgent = hasUserAnyAuthority([ROLES.ROLE_AGENT]) && !isAdmin;

  const [parentAgent, setParentAgent] = useState<any>(null);

  useEffect(() => {
    dispatch(resetAgentForm());
    dispatch(resetAgentLogoImage());
  }, [dispatch]);

  useEffect(() => {
    if (!isAgent) return;

    if (!account) {
      dispatch(getAccount() as any);
      return;
    }

    /**
     * Depending on your getAccount response, the logged-in agent data may be:
     * - account
     * - account.agent
     * - account.agency
     *
     * This keeps it flexible.
     */
    const parent =
      account?.agent ||
      account?.agency ||
      account?.agentDetails ||
      account;

    setParentAgent(parent);

    if (parent?.companyName && !agentForm.companyName) {
      dispatch(
        fillAgentFormField({
          field: "companyName",
          value: parent.companyName,
        })
      );
    }
  }, [isAgent, account, agentForm.companyName, dispatch]);

  useEffect(() => {
    let unblock: any;

    if (isQuickPanelOpen) {
      unblock = history.block(() => false);
    }

    return () => {
      if (unblock) {
        unblock();
      }
    };
  }, [isQuickPanelOpen, history]);

  const optionalNumber = Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null) {
        return null;
      }

      return value;
    })
    .notRequired();

  const validationSchema = useMemo(() => {
    const schema: any = {
      fullName: Yup.string()
        .trim()
        .required(
          t(
            "app.main.agency.validation.fullNameRequired",
            "Full name is required"
          )
        ),

      agencyName: Yup.string()
        .trim()
        .required(
          t(
            "app.main.agency.validation.agencyNameRequired",
            "Agency name is required"
          )
        ),

      phoneNumber: Yup.string()
        .trim()
        .required(
          t(
            "app.main.agency.validation.phoneNumberRequired",
            "Phone number is required"
          )
        ),

      contactEmail: Yup.string()
        .trim()
        .email(
          t(
            "app.main.agency.validation.invalidEmail",
            "Invalid email address"
          )
        )
        .required(
          t(
            "app.main.agency.validation.contactEmailRequired",
            "Contact email is required"
          )
        ),

      companyName: Yup.string()
        .trim()
        .required(
          t(
            "app.main.agency.validation.companyNameRequired",
            "Company name is required"
          )
        ),

      email: Yup.string()
        .trim()
        .email(
          t(
            "app.main.agency.validation.invalidEmail",
            "Invalid email address"
          )
        )
        .required(
          t(
            "app.main.agency.validation.identificationEmailRequired",
            "Identification email is required"
          )
        ),

      password: Yup.string()
        .trim()
        .required(
          t(
            "app.main.agency.validation.passwordRequired",
            "Password is required"
          )
        ),

      /**
       * These are optional for both Agent and Sub-Agent based on what you said.
       */
      address: Yup.string().nullable().notRequired(),
      location: Yup.string().nullable().notRequired(),

      /**
       * Privileges are optional for sub-agent.
       */
      scopes: Yup.array().notRequired(),
      privileges: Yup.array().notRequired(),
    };

    if (isAdmin) {
      /**
       * Required only when ADMIN creates a normal Agent.
       */
      schema.maxAdultAllowed = Yup.number()
        .typeError("Reservation Max Adult is required")
        .required("Reservation Max Adult is required")
        .min(1, "Cannot be negative or zero");

      schema.maxChildAllowed = Yup.number()
        .typeError("Reservation Max Child is required")
        .required("Reservation Max Child is required")
        .min(0, "Cannot be negative");

      schema.maxInfantAllowed = Yup.number()
        .typeError("Reservation Max Infant is required")
        .required("Reservation Max Infant is required")
        .min(0, "Cannot be negative");

      schema.balanceRequests = Yup.array().of(
        Yup.object().shape({
          creditLimit: Yup.number()
            .typeError("Maximum credit is required")
            .required("Maximum credit is required")
            .min(1, "Cannot be negative or zero"),

          currencyId: Yup.string().required("Currency is required"),
        })
      );

      schema.provisionForms = Yup.array()
        .of(
          Yup.object().shape({
            currencyId: Yup.string().required(
              t(
                "app.main.agency.validation.currencyRequired",
                "Currency is required"
              )
            ),
          })
        )
        .min(1, "At least one provision is required");
    } else {
      /**
       * For Sub-Agent creation, these fields are not entered by the user.
       * They will be inherited from the parent agent during submit.
       */
      schema.maxAdultAllowed = optionalNumber;
      schema.maxChildAllowed = optionalNumber;
      schema.maxInfantAllowed = optionalNumber;
      schema.allowInfantOnly = Yup.boolean().notRequired();

      schema.balanceRequests = Yup.array().notRequired();
      schema.provisionForms = Yup.array().notRequired();
      schema.discounts = Yup.array().notRequired();
      schema.debtRequests = Yup.array().notRequired();
    }

    return Yup.object().shape(schema);
  }, [isAdmin, optionalNumber, t]);

  const removeFrontendId = (items: any[] = []) => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(({ id, ...rest }) => rest);
  };

  const normalizeProvisionForms = (items: any[] = []) => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(({ id, currency, ...rest }) => ({
      ...rest,
      currencyId: rest.currencyId || currency?.id,
    }));
  };

  const normalizeBalanceRequests = (items: any[] = []) => {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(({ id, currency, ...rest }) => ({
      ...rest,
      currencyId: rest.currencyId || currency?.id,
    }));
  };

  const getValue = (...values: any[]) => {
    return values.find(
      (value) => value !== undefined && value !== null && value !== ""
    );
  };

  const buildPayload = () => {
    const payload: any = {
      ...agentForm,
      balanceRequests: normalizeBalanceRequests(agentForm.balanceRequests),
      provisionForms: normalizeProvisionForms(agentForm.provisionForms),
      discounts: removeFrontendId(agentForm.discounts),
    };

    if (isAgent) {
      const parent = parentAgent || account?.agent || account?.agency || account;

      const inheritedMaxAdultAllowed = getValue(
        parent?.maxAdultAllowed,
        agentForm.maxAdultAllowed
      );

      const inheritedMaxChildAllowed = getValue(
        parent?.maxChildAllowed,
        agentForm.maxChildAllowed,
        0
      );

      const inheritedMaxInfantAllowed = getValue(
        parent?.maxInfantAllowed,
        agentForm.maxInfantAllowed,
        0
      );

      const inheritedAllowInfantOnly = Boolean(
        getValue(parent?.allowInfantOnly, agentForm.allowInfantOnly, false)
      );

      const inheritedProvisionForms = normalizeProvisionForms(
        parent?.provisionForms || agentForm.provisionForms
      );

      const inheritedBalanceRequests = normalizeBalanceRequests(
        parent?.balanceRequests || agentForm.balanceRequests
      );

      const inheritedDiscounts = removeFrontendId(
        parent?.discounts || agentForm.discounts
      );

      payload.maxAdultAllowed = inheritedMaxAdultAllowed;
      payload.maxChildAllowed = inheritedMaxChildAllowed;
      payload.maxInfantAllowed = inheritedMaxInfantAllowed;
      payload.allowInfantOnly = inheritedAllowInfantOnly;

      payload.provisionForms = inheritedProvisionForms;

      /**
       * Keep these inherited too if backend expects them.
       * If backend does not need them, it will usually ignore them.
       */
      payload.balanceRequests = inheritedBalanceRequests;
      payload.discounts = inheritedDiscounts;

      /**
       * Privileges/scopes are optional for Sub-Agent.
       * Do not send empty arrays if backend treats them badly.
       */
      if (!payload.scopes || payload.scopes.length === 0) {
        delete payload.scopes;
      }

      if (!payload.privileges || payload.privileges.length === 0) {
        delete payload.privileges;
      }

      /**
       * Defensive checks before sending the request.
       * This prevents backend 400 and shows a clear frontend message.
       */
      if (
        payload.maxAdultAllowed === undefined ||
        payload.maxAdultAllowed === null ||
        payload.maxAdultAllowed === ""
      ) {
        throw new Error(
          "Parent agent is missing Reservation Max Adult. Cannot create sub-agent."
        );
      }

      if (
        payload.maxInfantAllowed === undefined ||
        payload.maxInfantAllowed === null ||
        payload.maxInfantAllowed === ""
      ) {
        throw new Error(
          "Parent agent is missing Reservation Max Infant. Cannot create sub-agent."
        );
      }

      if (
        !Array.isArray(payload.provisionForms) ||
        payload.provisionForms.length === 0
      ) {
        throw new Error(
          "Parent agent is missing provision forms. Cannot create sub-agent."
        );
      }
    }

    return payload;
  };

  const title = isAgent
    ? t("app.main.agency.create.subAgentTitle", "Register Sub-Agent")
    : t("app.main.agency.create.title", "Register agent");

  return (
    <div className={classes.formWrapper}>
      <div className={classes.formHeader} style={{ marginBottom: "35px" }}>
        <h1 style={{ color: "#0D3B66" }}>{title}</h1>
      </div>

      <Formik
        initialValues={agentForm}
        validationSchema={validationSchema}
        enableReinitialize
        validateOnBlur
        validateOnChange
        onSubmit={async () => {
          try {
            const payload = buildPayload();

            const res = await AgentService.createAgent(payload);

            if (agentLogoImage.logoImage) {
              const file: File = await AppUtils.dataUrlToFile(
                agentLogoImage.logoImage,
                "logoImage"
              );

              dispatch(updateAgentImage(res.data.id, file));
            }

            dispatch(isAgent ? fetchAllSubAgents() : fetchAllAgents());
            dispatch(toggleQuickPanel());
            dispatch(setSuccess(true));

            dispatch(
              successMsg(
                localStorage.getItem("i18nextLng") === "en"
                  ? isAgent
                    ? "Sub-Agent added successfully!"
                    : "Agent added successfully!"
                  : isAgent
                  ? "Sub-Agjenti u shtua me sukses!"
                  : "Agenti u shtua me sukses!"
              )
            );
          } catch (error: any) {
            const message =
              error?.response?.data?.message ||
              error?.data?.message ||
              error?.message ||
              t("app.common.errors.generic", "Something went wrong");

            dispatch(errorMsg(message));
            dispatch(setError(message));
          }
        }}
      >
        {({ errors, touched }) => (
          <Form>
            <AgentInformation errors={errors} touched={touched} />

            {isAdmin && (
              <>
                <InvoiceInfo />

                <ReservationInfo errors={errors} touched={touched} />

                <Credits errors={errors} touched={touched} />

                <ProvisionInfo errors={errors} touched={touched} />

                <DiscountInfo />

                <DebtInfo />
              </>
            )}

            <ScopesInfo />

            <Grid
              container
              justify="flex-end"
              alignItems="center"
              style={{ marginTop: "20px" }}
            >
              <Grid item>
                <LoadingButton
                  type="submit"
                  btnTitle={t(
                    "app.main.agency.agentsList.registerButton",
                    "Register"
                  )}
                  size="medium"
                  isLoading={createAgentLoading}
                />

                <Button
                  onClick={() => {
                    dispatch(toggleQuickPanel());
                    dispatch(resetAgentForm());
                    history.push("/agents-agencies/list");
                  }}
                >
                  {t("app.main.schedules.splitPassenger.closeButton", "Close")}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateAgent;